import { Capacitor } from '@capacitor/core';
import type { IAdProvider, AdReward, AdRewardType, AdStatus, AdProviderConfig } from './AdProvider.js';
import { MockAdProvider } from './MockAdProvider.js';
import { AdMobProvider } from './AdMobProvider.js';
import { AppsInTossProvider } from './AppsInTossProvider.js';
import { t } from '../i18n/index.js';

export type AdProviderType = 'mock' | 'admob' | 'appintoss' | 'auto';

export function getAdRewards() {
  return {
    CPS_BOOST: { type: 'boost' as const, amount: 30, description: t('ad_cps_boost') },
    OFFLINE_DOUBLE: { type: 'ip' as const, amount: 2, description: t('ad_offline_double') },
    PRESTIGE_BONUS: { type: 'boost' as const, amount: 50, description: t('ad_prestige_bonus') },
    TIMESKIP: { type: 'ip' as const, amount: 7200, description: t('ad_timeskip') },
    FREE_DOCS: { type: 'docs' as const, amount: 1, description: t('ad_free_docs') },
    GOLDEN_DOUBLE: { type: 'boost' as const, amount: 2, description: t('ad_golden_double') },
    START_BOOST: { type: 'boost' as const, amount: 60, description: t('ad_start_boost') },
    INCOME_X5: { type: 'ip' as const, amount: 5, description: t('ad_income_x5') },
    TAP_X10: { type: 'boost' as const, amount: 10, description: t('ad_tap_x10') },
  };
}

class AdManagerClass {
  private provider: IAdProvider | null = null;
  private _config: AdProviderConfig = {};
  private _isInitialized = false;
  private autoPreloadEnabled = true;

  private isAppsInTossEnvironment(): boolean {
    if (typeof window !== 'undefined') {
      const ua = navigator.userAgent.toLowerCase();
      if (ua.includes('toss') || ua.includes('appintoss')) return true;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((window as any).__APPS_IN_TOSS__ || (window as any).AppsInToss) return true;
    }
    return false;
  }

  get isInitialized(): boolean { return this._isInitialized; }
  get config(): AdProviderConfig { return this._config; }

  async initialize(providerType: AdProviderType = 'auto', config: AdProviderConfig = {}): Promise<boolean> {
    this._config = config;

    if (providerType === 'auto') {
      if (this.isAppsInTossEnvironment()) {
        providerType = 'appintoss';
      } else if (Capacitor.isNativePlatform()) {
        providerType = 'admob';
      } else {
        providerType = 'mock';
      }
    }

    switch (providerType) {
      case 'admob': this.provider = new AdMobProvider(config); break;
      case 'appintoss': this.provider = new AppsInTossProvider(config); break;
      case 'mock': default: this.provider = new MockAdProvider(); break;
    }

    const success = await this.provider.initialize();
    if (success) {
      this._isInitialized = true;
      console.log(`[AdManager] Initialized with ${this.provider.name}`);
      if (this.autoPreloadEnabled) this.preloadRewardedAd();
    }
    return success;
  }

  getStatus(): AdStatus {
    return this.provider?.status ?? 'idle';
  }

  isRewardedAdReady(): boolean {
    return this.provider?.isRewardedAdReady() ?? false;
  }

  async preloadRewardedAd(rewardType?: AdRewardType): Promise<boolean> {
    if (!this.provider) return false;
    return this.provider.loadRewardedAd(rewardType);
  }

  async showRewardedAd(
    reward: AdReward,
    onReward?: (reward: AdReward) => void,
    onError?: (error: string) => void
  ): Promise<AdReward | null> {
    if (!this.provider) {
      onError?.('Ad provider not initialized');
      return null;
    }

    if (!this.provider.isRewardedAdReady()) {
      const loaded = await this.provider.loadRewardedAd(reward.type);
      if (!loaded) {
        onError?.('Failed to load ad');
        return null;
      }
    }

    this.provider.setReward(reward);
    const earnedReward = await this.provider.showRewardedAd(reward.type);

    if (earnedReward) {
      onReward?.(earnedReward);
    } else {
      onError?.('Ad not completed');
    }

    if (this.autoPreloadEnabled) {
      this.preloadRewardedAd(reward.type);
    }

    return earnedReward;
  }

  destroy(): void {
    this.provider?.destroy();
    this.provider = null;
    this._isInitialized = false;
  }
}

export const AdManager = new AdManagerClass();
